/**
 * Licensed Materials - Property of IBM
 * 5737-M66, 5900-AAA, 5900-A0N, 5725-S86, 5737-I75
 * © Copyright IBM Corp. 2020, 2023 All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication, or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */

import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { DateTimePicker } from 'carbon-addons-iot-react';
import moment from 'moment';
import omit from 'lodash/omit';

import {
  PickerKinds,
  TimeRanges,
  TimeRangeValue,
  TimeRangeOption,
  PALTimeRange,
  PALPresetTimeRange,
  PALAbsoluteTimeRange,
  PALRelativeTimeRange,
} from '../../../types/DateTypes';

const messages = defineMessages({
  last30Minutes: 'Last 30 minutes',
  lastHour: 'Last hour',
  last6Hours: 'Last 6 hours',
  last12Hours: 'Last 12 hours',
  last24Hours: 'Last 24 hours',
  to: 'to',
  toNow: 'to Now',
  calendar: 'Calendar',
  minutes: 'minutes',
  hours: 'hours',
  days: 'days',
  weeks: 'weeks',
  months: 'months',
  years: 'years',
  today: 'Today',
  yesterday: 'Yesterday',
  customRange: 'Custom Range',
  relative: 'Relative',
  last: 'Last',
  invalidNumber: 'Number is not valid',
  relativeTo: 'Relative to',
  absolute: 'Absolute',
  startTime: 'Start time',
  endTime: 'End time',
  applyBtn: 'Apply',
  cancelBtn: 'Cancel',
  backBtn: 'Back',
  increment: 'Increment',
  decrement: 'Decrement',
  number: 'number',
});

type onDateChangeType = (date: { startDate: number; endDate: number }) => void;

type TableDateSelectorType = {
  /** callback when a timerange is selected */
  onDateChange: onDateChangeType;
  timeRangeOptions: TimeRangeOption[];
  selectedTimeRange: PALTimeRange;
  setSelectedTimeRange: React.Dispatch<React.SetStateAction<PALTimeRange>>;
};

export const handleDateRangeChange = (
  timeRangeId: TimeRanges,
  onDateChange: onDateChangeType
): void => {
  const endDate = moment();
  let startDate = endDate.clone().subtract(1, 'day');

  switch (timeRangeId) {
    case 'last30Minutes':
      startDate = endDate.clone().subtract(30, 'minutes');
      break;
    case 'lastHour':
      startDate = endDate.clone().subtract(1, 'hour');
      break;
    case 'last6Hours':
      startDate = endDate.clone().subtract(6, 'hours');
      break;
    case 'last12Hours':
      startDate = endDate.clone().subtract(12, 'hours');
      break;
    case 'last24Hours':
      startDate = endDate.clone().subtract(1, 'day');
      break;
    default:
      break;
  }

  onDateChange({
    startDate: startDate.valueOf(),
    endDate: endDate.valueOf(),
  });
};

export const handleCustomDateRangeChange = (
  dateTimeEvt: TimeRangeValue,
  onDateChange: onDateChangeType
): void => {
  const endDate = moment(dateTimeEvt.end);
  const startDate = moment(dateTimeEvt.start);

  onDateChange({
    startDate: startDate.valueOf(),
    endDate: endDate.valueOf(),
  });
};

export const onApply =
  (
    timeRangeOptions: TimeRangeOption[],
    setSelectedTimeRange: React.Dispatch<React.SetStateAction<PALTimeRange>>,
    onDateChange: onDateChangeType
  ) =>
  (selectedTimeRange: PALTimeRange): void => {
    const presetTimeRange = selectedTimeRange as PALPresetTimeRange;
    const absoluteTimeRange = selectedTimeRange as PALAbsoluteTimeRange;
    const relativeTimeRange = selectedTimeRange as PALRelativeTimeRange;

    // Preset time range
    if (
      presetTimeRange.timeRangeKind === PickerKinds.PRESET &&
      presetTimeRange.timeRangeValue?.id
    ) {
      setSelectedTimeRange({
        timeRangeKind: presetTimeRange.timeRangeKind,
        timeRangeValue: omit(
          timeRangeOptions.find(({ id }) => id === presetTimeRange.timeRangeValue.id),
          'start',
          'end'
        ),
      });
      handleDateRangeChange(presetTimeRange.timeRangeValue.id, onDateChange);
    }
    // custom time range
    else if (
      absoluteTimeRange.timeRangeKind === PickerKinds.ABSOLUTE &&
      absoluteTimeRange.timeRangeValue
    ) {
      setSelectedTimeRange({
        timeRangeKind: absoluteTimeRange.timeRangeKind,
        timeRangeValue: omit(absoluteTimeRange.timeRangeValue, 'start', 'end'),
      } as PALTimeRange);
      handleCustomDateRangeChange(absoluteTimeRange.timeRangeValue, onDateChange);
    } else if (
      relativeTimeRange.timeRangeKind === PickerKinds.RELATIVE &&
      relativeTimeRange.timeRangeValue
    ) {
      setSelectedTimeRange({
        timeRangeKind: relativeTimeRange.timeRangeKind,
        timeRangeValue: relativeTimeRange.timeRangeValue,
      });
      handleCustomDateRangeChange(relativeTimeRange.timeRangeValue, onDateChange);
    }
  };

const TableDateSelector = ({
  onDateChange,
  timeRangeOptions,
  selectedTimeRange,
  setSelectedTimeRange,
}: TableDateSelectorType): JSX.Element => {
  const { formatMessage, locale } = useIntl();

  const RELATIVE_VALUES = {
    YESTERDAY: 'YESTERDAY',
    TODAY: 'TODAY',
  };

  return (
    <DateTimePicker
      id="table-data--date-picker"
      testId="table-data--date-picker"
      dateTimeMask="L HH:mm"
      locale={locale?.split('-')?.[0]}
      showRelativeOption
      defaultValue={selectedTimeRange}
      presets={timeRangeOptions}
      relatives={[
        {
          label: formatMessage(messages.today),
          value: RELATIVE_VALUES.TODAY,
        },
        {
          label: formatMessage(messages.yesterday),
          value: RELATIVE_VALUES.YESTERDAY,
        },
      ]}
      onApply={onApply(timeRangeOptions, setSelectedTimeRange, onDateChange)}
      i18n={{
        toLabel: formatMessage(messages.to),
        toNowLabel: formatMessage(messages.toNow),
        calendarLabel: formatMessage(messages.calendar),
        presetLabels: [
          formatMessage(messages.last30Minutes),
          formatMessage(messages.lastHour),
          formatMessage(messages.last6Hours),
          formatMessage(messages.last12Hours),
          formatMessage(messages.last24Hours),
        ],
        intervalLabels: [
          formatMessage(messages.minutes),
          formatMessage(messages.hours),
          formatMessage(messages.days),
          formatMessage(messages.weeks),
          formatMessage(messages.months),
          formatMessage(messages.years),
        ],
        relativeLabels: [formatMessage(messages.today), formatMessage(messages.yesterday)],
        customRangeLinkLabel: formatMessage(messages.customRange),
        customRangeLabel: formatMessage(messages.customRange),
        relativeLabel: formatMessage(messages.relative),
        lastLabel: formatMessage(messages.last),
        invalidNumberLabel: formatMessage(messages.invalidNumber),
        relativeToLabel: formatMessage(messages.relativeTo),
        absoluteLabel: formatMessage(messages.absolute),
        startTimeLabel: formatMessage(messages.startTime),
        endTimeLabel: formatMessage(messages.endTime),
        applyBtnLabel: formatMessage(messages.applyBtn),
        cancelBtnLabel: formatMessage(messages.cancelBtn),
        backBtnLabel: formatMessage(messages.backBtn),
        increment: formatMessage(messages.increment),
        decrement: formatMessage(messages.decrement),
        hours: formatMessage(messages.hours),
        minutes: formatMessage(messages.minutes),
        number: formatMessage(messages.number),
      }}
      hasTimeInput
    />
  );
};

export default TableDateSelector;
